import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import EditIcon from "@mui/icons-material/Edit";
import InsertLink from "@mui/icons-material/InsertLink";
import LinkOff from "@mui/icons-material/LinkOff";
import { Divider, IconButton, Link, Skeleton, Tooltip, Typography } from "@mui/material";
import { COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from "lexical";
import { useEffect, useState } from "react";
import { useFlag } from "../../../../../server/optimizely";
import { ConditionalTooltip } from "../../../../generic/components/conditional-tooltip";
import { getSurveyIdFromUrl } from "../../components/utils/survey-url";
import { useLexicalContext } from "../../lexical-context";
import { REMOVE_LINK_COMMAND } from "../../types/commands";
import { $multipleElementsSelected } from "../../utils/multiple-elements-selected";
import { floatingToolbarStyles } from "./floating-toolbar-styles";
import { useLinkButtonOnClick } from "./hooks/use-link-button-on-click";
import { useLinkTitle } from "./hooks/use-link-title";
import { ToolbarIconButton } from "./toolbar-icon-button";

import type { LinkType } from "../../components/button/types";
import type { EditorModalState } from "../../lexical-context";
import type { LinkTarget } from "../../nodes/link-target-state";
import type { ToolbarProps } from "./props";
import type { FC } from "react";

export const LinkButton: FC<
  ToolbarProps & {
    handleOpen: (data: EditorModalState) => void;
    active: boolean;
    url?: string;
    href?: string;
    target?: LinkTarget;
  }
> = ({ handleOpen, disabled, mode, active, url, href, target }) => {
  const [forceDisabled, setForceDisabled] = useState(false);
  const [editor] = useLexicalComposerContext();
  const { format } = useLexicalContext();
  const [showLinkDetails] = useFlag("cha-4664-show-link-details-edit-and-unlink");

  const linkButtonOnClick = useLinkButtonOnClick(editor, handleOpen);

  useEffect(
    () =>
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          setForceDisabled($multipleElementsSelected());

          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  // The SMS top-level toolbar shows a labelled "Add link" button; everywhere else (and the
  // floating edit toolbar) keeps the compact icon-only button.
  if (format === "sms" && mode === "toolbar") {
    return (
      <ToolbarIconButton
        data-analytics-id="sms-editor-add-link"
        disabled={disabled || forceDisabled}
        data-testid={`${mode}-button-link`}
        label="Add Link"
        onClick={linkButtonOnClick}
        icon={<InsertLink sx={floatingToolbarStyles[mode].formatIcon} />}
      />
    );
  }

  const showUrl = showLinkDetails && mode === "floating" && Boolean(url);

  return (
    <>
      {showUrl && <Divider orientation="vertical" flexItem sx={{ mr: 0.25 }} />}
      <Tooltip title="Link">
        <IconButton
          data-analytics-id={format === "sms" ? "sms-editor-add-link" : `lexical-${mode}-link`}
          disabled={disabled || forceDisabled}
          data-testid={`${mode}-button-link`}
          aria-label="Link"
          data-active={active}
          aria-pressed={active}
          tabIndex={-1}
          onClick={linkButtonOnClick}
          sx={{
            ...floatingToolbarStyles[mode].button,
            backgroundColor: active ? "background.active" : "background.neutral",
          }}
        >
          <InsertLink sx={floatingToolbarStyles[mode].formatIcon} />
        </IconButton>
      </Tooltip>
      {showUrl && url && (
        <>
          {/* Keyed on the link so moving to another link starts a fresh name lookup, rather than
              showing the previous link's name until it reloads */}
          <FloatingLinkTitle
            key={`${url}|${target?.id ?? ""}`}
            url={url}
            href={href}
            target={target}
          />
          <Tooltip title="Edit link">
            <IconButton
              disabled={disabled || forceDisabled}
              data-analytics-id="lexical-floating-edit-link"
              data-testid="floating-button-edit-link"
              aria-label="Edit link"
              tabIndex={-1}
              onClick={linkButtonOnClick}
              sx={floatingToolbarStyles.floating.button}
            >
              <EditIcon sx={floatingToolbarStyles.floating.formatIcon} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove link">
            <IconButton
              disabled={disabled || forceDisabled}
              data-analytics-id="lexical-floating-remove-link"
              data-testid="floating-button-remove-link"
              aria-label="Remove link"
              tabIndex={-1}
              onClick={() => editor.dispatchCommand(REMOVE_LINK_COMMAND, null)}
              sx={floatingToolbarStyles.floating.button}
            >
              <LinkOff sx={floatingToolbarStyles.floating.formatIcon} />
            </IconButton>
          </Tooltip>
        </>
      )}
    </>
  );
};

const MAX_LINK_TITLE_LENGTH = 30;

// Matches the link dialog's target options
const LINK_TYPE_LABELS: Record<LinkType["type"], string> = {
  link_url: "URL",
  link_survey: "Survey",
  link_lib: "Content Library",
  link_email: "Email Communication",
};

const FloatingLinkTitle: FC<{ url: string; href?: string; target?: LinkTarget }> = ({
  url,
  href,
  target,
}) => {
  const { title, loading } = useLinkTitle(url, target);
  // Survey URLs are templated per recipient, so there is no page worth opening from here
  const isSurvey = getSurveyIdFromUrl(url) !== null;
  const type = target?.type ?? (isSurvey ? "link_survey" : "link_url");
  const text = title ?? url;
  const isLong = text.length > MAX_LINK_TITLE_LENGTH;

  return (
    <>
      <Typography
        data-testid="floating-link-type"
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ alignSelf: "center", ml: 0.75, mr: 1 }}
      >
        {LINK_TYPE_LABELS[type]}
      </Typography>
      {type !== "link_url" && loading ? (
        <Skeleton
          data-testid="floating-link-loading"
          width={120}
          sx={{ alignSelf: "center", mr: 0.5 }}
        />
      ) : (
        <ConditionalTooltip situations={[{ title: text, condition: isLong }]}>
          <Link
            data-testid="floating-link-url"
            // A plain anchor: the theme's default Link routes anything that isn't https:// in-app, and
            // needs an href
            component="a"
            href={isSurvey ? undefined : href}
            target="_blank"
            rel="noopener noreferrer"
            noWrap
            variant="body2"
            sx={{ alignSelf: "center", mr: 0.5 }}
          >
            {isLong ? `${text.slice(0, MAX_LINK_TITLE_LENGTH)}…` : text}
          </Link>
        </ConditionalTooltip>
      )}
    </>
  );
};
